import { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { TimerStatus } from 'shovel-lib/types';
import { getActiveTimer, getTimerMode } from '@state/timer/selectors';
import { getElapsedSeconds, readTimer, timerTabTitle } from '../utils/timer';
import t from '../i18n/t';

const TICK_MS = 1000;

/**
 * While a timer runs, the tab says how it's going when Shovel isn't the tab in view (timer spec step 6, Petr
 * 2026-10-29): "42m left · Week 2 — Shovel", "+5m over · …", "Paused · …". Back to "Shovel" the moment the student
 * looks, as the pill has the time then. Analytics (GA through Tag Manager) read the title as the page is used, so they
 * keep seeing "Shovel" rather than a new page name every minute. Installed (standalone) Shovel windows show it in their
 * title bar the same way; phones show no title.
 *
 * Written straight to the document, never through React state, so the app doesn't redraw for it.
 */
export function useTimerTabTitle() {
  const timer = useSelector(getActiveTimer);
  const mode = useSelector(getTimerMode);

  useEffect(() => {
    if (!timer) return;
    const ticking = timer.status === TimerStatus.ACTIVE;
    const show = (title: string) => {
      if (document.title !== title) document.title = title;
    };
    const update = () => {
      if (!document.hidden) return show(t.SHOVEL);
      const usedSeconds = getElapsedSeconds(timer.timePassedInSeconds || 0, timer.startedInUtc, ticking);
      const reading = readTimer({ usedSeconds, plannedMinutes: timer.plannedMinutes, mode });
      show(timerTabTitle({ reading, paused: !ticking, name: timer.title }));
    };
    // Back in view: "Shovel" at once. Out of view: the time comes with the next tick, after anything listening for the
    // page being left has read the title.
    const onVisibility = () => {
      if (!document.hidden) show(t.SHOVEL);
    };

    update();
    const interval = window.setInterval(update, TICK_MS);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      window.clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisibility);
      show(t.SHOVEL);
    };
  }, [timer, mode]);
}
